<script>
import { apiSecond } from '@/boot/axios'
import './admin.css'
import UpdateIcon from '@/components/system-updates/UpdateIcon.vue'
import {
	ICON_KEYS,
	LIMITS,
	htmlTextLength,
	categoryBadgeStyle,
	formatUpdateDateTime,
	apiErrorMessage,
} from '@/components/system-updates/utils'

const LANGS = ['ru', 'kz']
const MAX_IMAGE_SIZE = 5 * 1024 * 1024
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const IMAGE_EXT_RE = /\.(jpe?g|png|webp)$/i
// Окно новизны на бэке: старше — не попадёт ни в окно при входе, ни в колокольчик
const NEW_WINDOW_DAYS = 30

// Набор, который бэк оставляет в HTML: p, br, strong/b, em/i, u, s, span, ul, ol, li,
// a, h2–h4, blockquote. Остальное он вырежет — в редакторе его просто нет.
const EDITOR_TOOLBAR = [
	[{ header: [2, 3, 4, false] }],
	['bold', 'italic', 'underline', 'strike'],
	[{ list: 'ordered' }, { list: 'bullet' }],
	['blockquote', 'link'],
	['clean'],
]

let uid = 0

function emptyHighlight() {
	return {
		uid: ++uid,
		icon: 'document',
		title_ru: '',
		title_kz: '',
		subtitle_ru: '',
		subtitle_kz: '',
		description_ru: '',
		description_kz: '',
	}
}

function emptyImage() {
	return { file: null, objectUrl: null, currentUrl: null, remove: false, failed: false }
}

function pad(n) {
	return String(n).padStart(2, '0')
}

// ISO с таймзоной → значение для <input type="datetime-local"> в часовом поясе браузера
function toLocalInput(value) {
	const date = value ? new Date(value) : new Date()
	if (Number.isNaN(date.getTime())) return ''
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
		date.getHours(),
	)}:${pad(date.getMinutes())}`
}

function fromLocalInput(value) {
	if (!value) return null
	const date = new Date(value)
	return Number.isNaN(date.getTime()) ? null : date
}

// Пустой редактор Quill отдаёт "<p><br></p>" — для бэка это «описания нет»
function cleanHtml(value) {
	const html = (value || '').trim()
	if (!html) return null
	const text = html
		.replace(/<[^>]*>/g, '')
		.replace(/&nbsp;/g, ' ')
		.trim()
	return text ? html : null
}

function cleanText(value) {
	const text = (value || '').trim()
	return text || null
}

export default {
	name: 'SystemUpdateForm',
	components: { UpdateIcon },
	data() {
		return {
			langs: LANGS,
			lang: 'ru',
			iconKeys: ICON_KEYS,
			limits: LIMITS,
			// Раскрытые пункты ключевых изменений, по uid
			openHighlights: {},
			editorToolbar: EDITOR_TOOLBAR,
			defaultCover: require('@/assets/img/system-updates/popup-cover.png'),

			categories: [],
			categoriesLoaded: false,

			loading: false,
			notFound: false,
			loadError: null,

			form: {
				category_id: null,
				title_ru: '',
				title_kz: '',
				summary_ru: '',
				summary_kz: '',
				description_ru: '',
				description_kz: '',
				show_popup: true,
				publish: false,
				published_at: toLocalInput(),
				highlights: [emptyHighlight()],
			},
			status: null,
			images: {
				cover: emptyImage(),
				preview: emptyImage(),
			},

			errors: {},
			formError: null,
			saving: false,
			snapshot: null,
		}
	},
	computed: {
		id() {
			const id = Number(this.$route.params.id)
			return Number.isInteger(id) && id > 0 ? id : null
		},
		isEdit() {
			return Boolean(this.$route.params.id)
		},
		canManage() {
			return this.$store.getters.userPermission(['manage_system_updates'])
		},
		canManageCategories() {
			return this.$store.getters.userPermission(['manage_system_update_categories'])
		},
		backLink() {
			const back = this.$route.query.back
			return typeof back === 'string' &&
				back.startsWith('/' + this.$i18n.locale + '/account/system-updates')
				? back
				: '/' + this.$i18n.locale + '/account/system-updates'
		},
		selectedCategory() {
			return this.categories.find((c) => c.id === this.form.category_id) || null
		},
		langErrors() {
			const result = { ru: false, kz: false }
			Object.keys(this.errors).forEach((key) => {
				if (key.endsWith('_ru')) result.ru = true
				if (key.endsWith('_kz')) result.kz = true
			})
			return result
		},
		publishDate() {
			return this.form.publish ? fromLocalInput(this.form.published_at) : null
		},
		publishHint() {
			if (!this.form.publish) return this.$t('system_updates.admin.publish_hint_draft')
			const date = this.publishDate
			if (!date) return ''
			if (date.getTime() > Date.now()) {
				return this.$t('system_updates.admin.publish_hint_scheduled', {
					date: formatUpdateDateTime(date.toISOString()),
				})
			}
			if (Date.now() - date.getTime() > NEW_WINDOW_DAYS * 24 * 3600 * 1000) {
				return this.$t('system_updates.admin.publish_hint_old', { days: NEW_WINDOW_DAYS })
			}
			return this.$t('system_updates.admin.publish_hint_now')
		},
		isDirty() {
			return this.snapshot !== null && this.snapshot !== this.serialize()
		},
		imageFields() {
			return [
				{
					key: 'cover',
					label: this.$t('system_updates.admin.field_cover'),
					hint: this.$t('system_updates.admin.cover_hint'),
				},
				{
					key: 'preview',
					label: this.$t('system_updates.admin.field_preview'),
					hint: this.$t('system_updates.admin.preview_hint'),
				},
			]
		},
	},
	watch: {
		// create → edit/:id после сохранения или переход на другое обновление
		'$route.params.id'() {
			this.init()
		},
	},
	mounted() {
		window.addEventListener('beforeunload', this.onBeforeUnload)
		this.loadCategories()
		this.init()
	},
	beforeDestroy() {
		window.removeEventListener('beforeunload', this.onBeforeUnload)
		Object.values(this.images).forEach((image) => {
			if (image.objectUrl) URL.revokeObjectURL(image.objectUrl)
		})
	},
	async beforeRouteLeave(to, from, next) {
		if (!this.isDirty || this.saving) return next()
		const leave = await this.$dialog.confirm({
			title: this.$t('system_updates.admin.leave_title'),
			text: this.$t('system_updates.admin.leave_text'),
			confirmText: this.$t('system_updates.admin.leave_confirm'),
		})
		next(leave)
	},
	methods: {
		categoryBadgeStyle,
		htmlTextLength,

		onBeforeUnload(event) {
			if (!this.isDirty) return
			event.preventDefault()
			event.returnValue = ''
		},

		serialize() {
			const images = {}
			Object.keys(this.images).forEach((key) => {
				const image = this.images[key]
				images[key] = [image.file ? image.file.name + image.file.size : null, image.remove]
			})
			const form = {
				...this.form,
				// uid — только ключ для v-for, в снимок его не берём
				highlights: this.form.highlights.map((h) => {
					const copy = { ...h }
					delete copy.uid
					return copy
				}),
			}
			form.description_ru = cleanHtml(form.description_ru)
			form.description_kz = cleanHtml(form.description_kz)
			form.highlights.forEach((h) => {
				h.description_ru = cleanHtml(h.description_ru)
				h.description_kz = cleanHtml(h.description_kz)
			})
			return JSON.stringify({ form, images })
		},

		async loadCategories() {
			try {
				if (this.canManageCategories) {
					const { data } = await apiSecond.get('v1/admin/system-update-categories')
					this.categories = (Array.isArray(data && data.data) ? data.data : []).map((c) => ({
						id: c.id,
						name: c.name_ru || c.name_kz || '#' + c.id,
						color: c.color,
					}))
				} else {
					const { data } = await apiSecond.get('v1/public/system-update-categories', {
						headers: { 'Accept-Language': 'ru' },
					})
					this.categories = Array.isArray(data && data.data) ? data.data : []
				}
			} catch (e) {
				this.categories = []
			} finally {
				this.categoriesLoaded = true
			}
		},

		resetForm() {
			this.form = {
				category_id: null,
				title_ru: '',
				title_kz: '',
				summary_ru: '',
				summary_kz: '',
				description_ru: '',
				description_kz: '',
				show_popup: true,
				publish: false,
				published_at: toLocalInput(),
				highlights: [emptyHighlight()],
			}
			this.status = null
			Object.keys(this.images).forEach((key) => this.resetImage(key))
			this.errors = {}
			this.formError = null
			this.lang = 'ru'
			this.openHighlights = { [this.form.highlights[0].uid]: true }
		},

		resetImage(key, currentUrl = null) {
			const image = this.images[key]
			if (image.objectUrl) URL.revokeObjectURL(image.objectUrl)
			this.images[key] = { ...emptyImage(), currentUrl }
		},

		async init() {
			this.notFound = false
			this.loadError = null
			this.resetForm()

			if (!this.isEdit) {
				this.snapshot = null
				this.$nextTick(() => {
					this.snapshot = this.serialize()
				})
				return
			}
			if (!this.id) {
				this.notFound = true
				return
			}

			this.loading = true
			this.snapshot = null
			try {
				const { data } = await apiSecond.get('v1/admin/system-updates/' + this.id)
				this.fill(data && data.data)
			} catch (error) {
				if (error && error.response && error.response.status === 404) this.notFound = true
				else this.loadError = apiErrorMessage(error, this.$t('system_updates.load_error'))
			} finally {
				this.loading = false
			}
		},

		fill(item) {
			if (!item) {
				this.notFound = true
				return
			}
			const highlights = Array.isArray(item.highlights) ? item.highlights : []
			this.form = {
				category_id: item.category_id || null,
				title_ru: item.title_ru || '',
				title_kz: item.title_kz || '',
				summary_ru: item.summary_ru || '',
				summary_kz: item.summary_kz || '',
				description_ru: item.description_ru || '',
				description_kz: item.description_kz || '',
				show_popup: Boolean(item.show_popup),
				publish: Boolean(item.published_at),
				published_at: toLocalInput(item.published_at),
				highlights: highlights.map((h) => ({
					uid: ++uid,
					icon: ICON_KEYS.includes(h.icon) ? h.icon : 'document',
					title_ru: h.title_ru || '',
					title_kz: h.title_kz || '',
					subtitle_ru: h.subtitle_ru || '',
					subtitle_kz: h.subtitle_kz || '',
					description_ru: h.description_ru || '',
					description_kz: h.description_kz || '',
				})),
			}
			this.status = item.status || null
			this.openHighlights = {}
			this.resetImage('cover', item.cover_url || null)
			this.resetImage('preview', item.preview_url || null)
			// Quill нормализует HTML при монтировании — снимок берём после него
			this.$nextTick(() => {
				setTimeout(() => {
					this.snapshot = this.serialize()
				}, 0)
			})
		},

		setPublishNow() {
			this.form.published_at = toLocalInput()
			this.clearError('published_at')
		},

		err(key) {
			return this.errors[key] || null
		},

		clearError(key) {
			if (this.errors[key]) this.$delete(this.errors, key)
		},

		// ---------- картинки ----------

		imageSrc(key) {
			const image = this.images[key]
			if (image.objectUrl) return image.objectUrl
			if (image.currentUrl && !image.remove && !image.failed) return image.currentUrl
			return key === 'cover' ? this.defaultCover : null
		},

		hasOwnImage(key) {
			const image = this.images[key]
			return Boolean(image.file || (image.currentUrl && !image.remove))
		},

		pickImage(key) {
			const input = this.$refs['file_' + key]
			const el = Array.isArray(input) ? input[0] : input
			if (el) el.click()
		},

		onImageChange(key, event) {
			const file = event.target.files && event.target.files[0]
			event.target.value = ''
			if (!file) return

			this.clearError(key)
			if (!IMAGE_TYPES.includes(file.type) && !IMAGE_EXT_RE.test(file.name)) {
				this.$set(this.errors, key, this.$t('system_updates.admin.error_image_type'))
				return
			}
			if (file.size > MAX_IMAGE_SIZE) {
				this.$set(this.errors, key, this.$t('system_updates.admin.error_image_size'))
				return
			}

			const image = this.images[key]
			if (image.objectUrl) URL.revokeObjectURL(image.objectUrl)
			image.file = file
			image.objectUrl = URL.createObjectURL(file)
			image.remove = false
		},

		removeImage(key) {
			const image = this.images[key]
			this.clearError(key)
			if (image.file) {
				if (image.objectUrl) URL.revokeObjectURL(image.objectUrl)
				image.file = null
				image.objectUrl = null
				return
			}
			image.remove = true
		},

		// ---------- ключевые изменения ----------

		isHighlightOpen(item) {
			return Boolean(this.openHighlights[item.uid])
		},

		toggleHighlight(item) {
			this.$set(this.openHighlights, item.uid, !this.openHighlights[item.uid])
		},

		highlightTitle(item) {
			return (item['title_' + this.lang] || item.title_ru || item.title_kz || '').trim()
		},

		hasHighlightError(index) {
			const prefix = 'highlights.' + index + '.'
			return Object.keys(this.errors).some((key) => key.startsWith(prefix))
		},

		addHighlight() {
			const item = emptyHighlight()
			this.form.highlights.push(item)
			this.$set(this.openHighlights, item.uid, true)
			this.clearError('highlights')
		},

		removeHighlight(index) {
			this.form.highlights.splice(index, 1)
			this.shiftHighlightErrors()
		},

		moveHighlight(index, delta) {
			const target = index + delta
			if (target < 0 || target >= this.form.highlights.length) return
			const list = this.form.highlights.slice()
			const [item] = list.splice(index, 1)
			list.splice(target, 0, item)
			this.form.highlights = list
			this.shiftHighlightErrors()
		},

		// Индексы ошибок пунктов после перестановки уже не совпадают — убираем их
		shiftHighlightErrors() {
			const errors = { ...this.errors }
			Object.keys(errors).forEach((key) => {
				if (key.startsWith('highlights.')) delete errors[key]
			})
			this.errors = errors
		},

		// ---------- сохранение ----------

		validate() {
			const errors = {}
			const required = this.$t('system_updates.admin.error_required')
			const max = (n) => this.$t('system_updates.admin.error_max', { max: n })
			const check = (key, value, limit, isRequired) => {
				const text = (value || '').trim()
				if (isRequired && !text) errors[key] = required
				else if (text.length > limit) errors[key] = max(limit)
			}

			if (!this.form.category_id) errors.category_id = required
			LANGS.forEach((lang) => {
				check('title_' + lang, this.form['title_' + lang], LIMITS.title, true)
				check('summary_' + lang, this.form['summary_' + lang], LIMITS.summary, false)
				if (htmlTextLength(this.form['description_' + lang]) > LIMITS.description) {
					errors['description_' + lang] = max(LIMITS.description)
				}
			})

			if (this.form.publish && !this.publishDate) {
				errors.published_at = this.$t('system_updates.admin.error_date')
			}

			if (this.form.show_popup && !this.form.highlights.length) {
				errors.highlights = this.$t('system_updates.admin.error_highlights_required')
			}
			this.form.highlights.forEach((h, index) => {
				const prefix = 'highlights.' + index + '.'
				if (!ICON_KEYS.includes(h.icon)) errors[prefix + 'icon'] = required
				LANGS.forEach((lang) => {
					check(prefix + 'title_' + lang, h['title_' + lang], LIMITS.highlightTitle, true)
					check(prefix + 'subtitle_' + lang, h['subtitle_' + lang], LIMITS.highlightSubtitle, false)
					if (htmlTextLength(h['description_' + lang]) > LIMITS.highlightDescription) {
						errors[prefix + 'description_' + lang] = max(LIMITS.highlightDescription)
					}
				})
			})

			Object.keys(this.images).forEach((key) => {
				if (this.errors[key]) errors[key] = this.errors[key]
			})

			this.errors = errors
			return !Object.keys(errors).length
		},

		focusFirstError() {
			const keys = Object.keys(this.errors)
			if (!keys.length) return
			// Ошибка только во втором языке — переключаемся на его вкладку
			const current = keys.some((key) => key.endsWith('_' + this.lang))
			const other = LANGS.find(
				(lang) => lang !== this.lang && keys.some((key) => key.endsWith('_' + lang)),
			)
			const langIndependent = keys.some((key) => !/_(ru|kz)$/.test(key))
			if (!current && !langIndependent && other) this.lang = other

			// Свёрнутый пункт с ошибкой раскрываем, иначе её не видно
			this.form.highlights.forEach((item, index) => {
				if (this.hasHighlightError(index)) this.$set(this.openHighlights, item.uid, true)
			})

			this.$nextTick(() => {
				const el = Array.from(
					this.$el.querySelectorAll('.has-error, .su-field__error, .su-admin-error'),
				).find((node) => node.offsetParent !== null)
				if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
			})
		},

		buildFormData() {
			const fd = new FormData()
			fd.append('category_id', String(this.form.category_id))
			LANGS.forEach((lang) => {
				fd.append('title_' + lang, this.form['title_' + lang].trim())
				fd.append('summary_' + lang, cleanText(this.form['summary_' + lang]) || '')
				fd.append('description_' + lang, cleanHtml(this.form['description_' + lang]) || '')
			})
			fd.append('show_popup', this.form.show_popup ? '1' : '0')
			fd.append('published_at', this.publishDate ? this.publishDate.toISOString() : '')

			Object.keys(this.images).forEach((key) => {
				const image = this.images[key]
				if (image.file) fd.append(key, image.file)
				else if (image.remove && image.currentUrl) fd.append('remove_' + key, '1')
			})

			fd.append(
				'highlights',
				JSON.stringify(
					this.form.highlights.map((h) => ({
						icon: h.icon,
						title_ru: h.title_ru.trim(),
						title_kz: h.title_kz.trim(),
						subtitle_ru: cleanText(h.subtitle_ru),
						subtitle_kz: cleanText(h.subtitle_kz),
						description_ru: cleanHtml(h.description_ru),
						description_kz: cleanHtml(h.description_kz),
					})),
				),
			)
			return fd
		},

		applyServerErrors(error) {
			const response = error && error.response
			const data = (response && response.data) || {}
			const fieldErrors = data.errors || data.error_messages

			if (response && response.status === 422 && fieldErrors && typeof fieldErrors === 'object') {
				const errors = {}
				Object.keys(fieldErrors).forEach((key) => {
					const messages = fieldErrors[key]
					errors[key] = Array.isArray(messages) ? messages[0] : String(messages)
				})
				this.errors = errors
				this.formError = this.$t('system_updates.admin.error_check_form')
			} else if (response && response.status === 404) {
				this.formError = this.$t('system_updates.admin.not_found')
			} else if (response && response.status === 403) {
				this.formError = this.$t('system_updates.admin.forbidden')
			} else if (response && response.status === 413) {
				this.formError = this.$t('system_updates.admin.error_image_size')
			} else {
				this.formError = apiErrorMessage(error, this.$t('system_updates.admin.save_error'))
			}
		},

		async save() {
			if (this.saving) return
			this.formError = null
			if (!this.validate()) {
				this.formError = this.$t('system_updates.admin.error_check_form')
				this.focusFirstError()
				return
			}

			this.saving = true
			try {
				const url = this.isEdit ? 'v1/admin/system-updates/' + this.id : 'v1/admin/system-updates'
				await apiSecond.post(url, this.buildFormData())

				// Снимок равен текущей форме — уход со страницы не спросит подтверждения
				this.snapshot = this.serialize()
				// Обратно в список с теми же фильтрами и страницей; params.saved —
				// сигнал списку показать «Обновление сохранено», в URL он не попадает
				const back = this.$router.resolve(this.backLink).route
				this.$router.push({
					name: 'SystemUpdatesAdmin',
					query: back.query,
					params: { lang: this.$route.params.lang || this.$i18n.locale, saved: '1' },
				})
			} catch (error) {
				this.applyServerErrors(error)
				this.focusFirstError()
			} finally {
				this.saving = false
			}
		},
	},
}
</script>

<template>
	<div class="wrap__content" :class="{ 'wrap__content--preloader': !$store.state.userObject }">
		<v-preloader v-if="!$store.state.userObject"></v-preloader>

		<div v-else class="container">
			<div class="row">
				<div class="col-xl-2 col-lg-3">
					<v-sidebar :active="['SystemUpdatesAdmin']"></v-sidebar>
				</div>
				<div class="col-xl-10 col-lg-9">
					<div class="content-wrapper">
						<div class="card__content">
							<div class="card__content-header">
								<div class="content__title">
									<div class="content__page--back">
										<router-link :to="backLink">
											<svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M13.125 5.25l-5.25 5.25 5.25 5.25" stroke="#52A5FC" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
										</router-link>
									</div>
									<div class="content__title--element">
										<div class="content__title--text">
											{{
												isEdit
													? $t('system_updates.admin.edit_title')
													: $t('system_updates.admin.create_title')
											}}
										</div>
									</div>
									<span v-if="status" class="ml-3" :class="'su-status su-status--' + status">
										{{ $t('system_updates.admin.status_' + status) }}
									</span>
								</div>
							</div>

							<div class="card__content--body">
								<div v-if="!canManage" class="su-admin-denied">
									{{ $t('system_updates.admin.forbidden') }}
								</div>

								<div v-else-if="loading" class="su-admin-denied">
									<v-progress-circular indeterminate color="#157FEE" />
								</div>

								<div v-else-if="notFound" class="su-admin-denied">
									<p>{{ $t('system_updates.admin.not_found') }}</p>
									<router-link :to="backLink" class="su-admin-link">
										{{ $t('system_updates.admin.back_to_list') }}
									</router-link>
								</div>

								<div v-else-if="loadError" class="su-admin-denied">
									<p>{{ loadError }}</p>
									<button type="button" class="su-admin-link" @click="init">
										{{ $t('system_updates.retry') }}
									</button>
								</div>

								<form v-else class="su-form" novalidate @submit.prevent="save">
									<div v-if="formError" class="su-admin-error">{{ formError }}</div>

									<!-- Основное и публикация -->
									<section class="su-form__section">
										<div class="su-form__grid">
											<div>
												<div class="su-field">
													<span class="su-field__label"
														>{{ $t('system_updates.admin.field_category') }} *</span
													>
													<div class="su-chips" :class="{ 'has-error': err('category_id') }">
														<button
															v-for="category in categories"
															:key="category.id"
															type="button"
															class="su-chip"
															:class="{ 'is-active': form.category_id === category.id }"
															:style="
																form.category_id === category.id
																	? categoryBadgeStyle(category)
																	: null
															"
															@click=";(form.category_id = category.id), clearError('category_id')"
														>
															{{ category.name }}
														</button>
														<span
															v-if="categoriesLoaded && !categories.length"
															class="su-field__hint"
														>
															{{ $t('system_updates.admin.no_categories') }}
															<router-link
																v-if="canManageCategories"
																:to="'/' + $i18n.locale + '/account/system-update-categories'"
																class="su-admin-link"
															>
																{{ $t('system_updates.admin.add_category') }}
															</router-link>
														</span>
													</div>
													<span v-if="err('category_id')" class="su-field__error">{{
														err('category_id')
													}}</span>
												</div>
												<label class="su-switch-row">
													<!-- Свой переключатель: без <v-app> у v-switch нет цветов темы -->
													<input
														v-model="form.show_popup"
														type="checkbox"
														class="su-toggle"
														@change="clearError('highlights')"
													/>
													<div>
														<div class="su-field__label">
															{{ $t('system_updates.admin.field_show_popup') }}
														</div>
														<div class="su-field__hint">
															{{ $t('system_updates.admin.show_popup_hint') }}
														</div>
													</div>
												</label>
											</div>
											<div class="su-field">
												<span class="su-field__label">{{
													$t('system_updates.admin.section_publish')
												}}</span>
												<div class="su-segment" role="radiogroup">
													<button
														type="button"
														class="su-segment__item"
														:class="{ 'is-active': !form.publish }"
														role="radio"
														:aria-checked="String(!form.publish)"
														@click=";(form.publish = false), clearError('published_at')"
													>
														{{ $t('system_updates.admin.publish_draft') }}
													</button>
													<button
														type="button"
														class="su-segment__item"
														:class="{ 'is-active': form.publish }"
														role="radio"
														:aria-checked="String(form.publish)"
														@click="form.publish = true"
													>
														{{ $t('system_updates.admin.publish_date') }}
													</button>
												</div>
												<div v-if="form.publish" class="su-date-row">
													<input
														v-model="form.published_at"
														class="su-field__input su-field__input--date"
														:class="{ 'has-error': err('published_at') }"
														type="datetime-local"
														@input="clearError('published_at')"
													/>
													<button type="button" class="su-admin-link" @click="setPublishNow">
														{{ $t('system_updates.admin.publish_now') }}
													</button>
												</div>
												<span v-if="err('published_at')" class="su-field__error">{{
													err('published_at')
												}}</span>
												<p v-else class="su-field__hint su-form__publish-hint">{{ publishHint }}</p>
											</div>
										</div>
									</section>

									<!-- Тексты -->
									<section class="su-form__section">
										<div class="su-form__section-head">
											<h3 class="su-form__section-title">
												{{ $t('system_updates.admin.section_texts') }}
											</h3>
											<div class="su-lang-tabs" role="tablist">
												<button
													v-for="code in langs"
													:key="code"
													type="button"
													role="tab"
													class="su-lang-tab"
													:class="{ 'is-active': lang === code }"
													:aria-selected="String(lang === code)"
													@click="lang = code"
												>
													{{ $t('system_updates.admin.lang_' + code) }}
													<span v-if="langErrors[code]" class="su-lang-tab__dot"></span>
												</button>
											</div>
										</div>

										<div v-for="code in langs" v-show="lang === code" :key="code">
											<label class="su-field">
												<span class="su-field__label"
													>{{ $t('system_updates.admin.field_title') }} *</span
												>
												<input
													v-model="form['title_' + code]"
													class="su-field__input"
													:class="{ 'has-error': err('title_' + code) }"
													type="text"
													:maxlength="limits.title"
													@input="clearError('title_' + code)"
												/>
												<span v-if="err('title_' + code)" class="su-field__error">{{
													err('title_' + code)
												}}</span>
												<span v-else class="su-field__hint"
													>{{ (form['title_' + code] || '').length }}/{{ limits.title }}</span
												>
											</label>

											<label class="su-field">
												<span class="su-field__label">{{
													$t('system_updates.admin.field_summary')
												}}</span>
												<textarea
													v-model="form['summary_' + code]"
													class="su-field__input"
													:class="{ 'has-error': err('summary_' + code) }"
													:maxlength="limits.summary"
													rows="3"
													@input="clearError('summary_' + code)"
												></textarea>
												<span v-if="err('summary_' + code)" class="su-field__error">{{
													err('summary_' + code)
												}}</span>
												<span v-else class="su-field__hint">
													{{ $t('system_updates.admin.summary_hint') }} ·
													{{ (form['summary_' + code] || '').length }}/{{ limits.summary }}
												</span>
											</label>

											<div class="su-field">
												<span class="su-field__label">{{
													$t('system_updates.admin.field_description')
												}}</span>
												<div class="su-editor" :class="{ 'has-error': err('description_' + code) }">
													<vue-editor
														v-model="form['description_' + code]"
														:editor-toolbar="editorToolbar"
														@text-change="clearError('description_' + code)"
													/>
												</div>
												<span v-if="err('description_' + code)" class="su-field__error">{{
													err('description_' + code)
												}}</span>
												<span v-else class="su-field__hint">
													{{ $t('system_updates.admin.description_hint') }} ·
													{{ htmlTextLength(form['description_' + code]) }}/{{ limits.description }}
												</span>
											</div>
										</div>
									</section>

									<!-- Ключевые изменения -->
									<section class="su-form__section">
										<div class="su-form__section-head">
											<h3 class="su-form__section-title">
												{{ $t('system_updates.admin.section_highlights') }}
											</h3>
											<div class="su-lang-tabs" role="tablist">
												<button
													v-for="code in langs"
													:key="code"
													type="button"
													role="tab"
													class="su-lang-tab"
													:class="{ 'is-active': lang === code }"
													@click="lang = code"
												>
													{{ $t('system_updates.admin.lang_' + code) }}
													<span v-if="langErrors[code]" class="su-lang-tab__dot"></span>
												</button>
											</div>
										</div>
										<p class="su-field__hint mb-3">
											{{ $t('system_updates.admin.highlights_hint') }}
										</p>
										<div v-if="err('highlights')" class="su-admin-error">
											{{ err('highlights') }}
										</div>

										<div
											v-for="(item, index) in form.highlights"
											:key="item.uid"
											class="su-highlight"
										>
											<div class="su-highlight__head" @click="toggleHighlight(item)">
												<span class="su-highlight__num">{{ index + 1 }}</span>
												<span class="su-highlight__icon"
													><UpdateIcon :name="item.icon" :size="18"
												/></span>
												<span
													class="su-highlight__title"
													:class="{ 'is-empty': !highlightTitle(item) }"
													>{{
														highlightTitle(item) || $t('system_updates.admin.hl_untitled')
													}}</span
												>
												<span
													v-if="hasHighlightError(index)"
													class="su-highlight__error-dot"
												></span>
												<div class="su-highlight__controls" @click.stop>
													<button
														type="button"
														class="su-icon-btn"
														:disabled="index === 0"
														:aria-label="$t('system_updates.admin.move_up')"
														@click="moveHighlight(index, -1)"
													>
														<svg
															width="16"
															height="16"
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															stroke-width="2.2"
															stroke-linecap="round"
															stroke-linejoin="round"
														>
															<path d="M12 19V5M6 11l6-6 6 6" />
														</svg>
													</button>
													<button
														type="button"
														class="su-icon-btn"
														:disabled="index === form.highlights.length - 1"
														:aria-label="$t('system_updates.admin.move_down')"
														@click="moveHighlight(index, 1)"
													>
														<svg
															width="16"
															height="16"
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															stroke-width="2.2"
															stroke-linecap="round"
															stroke-linejoin="round"
														>
															<path d="M12 5v14M6 13l6 6 6-6" />
														</svg>
													</button>
													<button
														type="button"
														class="su-icon-btn su-icon-btn--danger"
														:aria-label="$t('system_updates.admin.remove_highlight')"
														@click="removeHighlight(index)"
													>
														<svg
															width="16"
															height="16"
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															stroke-width="2"
															stroke-linecap="round"
															stroke-linejoin="round"
														>
															<path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
														</svg>
													</button>
												</div>
												<button
													type="button"
													class="su-icon-btn su-highlight__chevron"
													:class="{ 'is-open': isHighlightOpen(item) }"
													:aria-expanded="String(isHighlightOpen(item))"
													:aria-label="
														isHighlightOpen(item)
															? $t('system_updates.collapse')
															: $t('system_updates.more')
													"
													@click.stop="toggleHighlight(item)"
												>
													<svg
														width="16"
														height="16"
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														stroke-width="2.2"
														stroke-linecap="round"
														stroke-linejoin="round"
													>
														<path d="m6 9 6 6 6-6" />
													</svg>
												</button>
											</div>
											<div v-show="isHighlightOpen(item)" class="su-highlight__body">
												<div class="su-field">
													<span class="su-field__label">{{
														$t('system_updates.admin.field_icon')
													}}</span>
													<div class="su-icons" role="radiogroup">
														<button
															v-for="key in iconKeys"
															:key="key"
															type="button"
															class="su-icons__item"
															:class="{ 'is-active': item.icon === key }"
															role="radio"
															:aria-checked="String(item.icon === key)"
															:aria-label="key"
															@click="item.icon = key"
														>
															<UpdateIcon :name="key" :size="20" />
														</button>
													</div>
												</div>
												<div v-for="code in langs" v-show="lang === code" :key="code">
													<div class="su-form__grid su-form__grid--tight">
														<label class="su-field">
															<span class="su-field__label"
																>{{ $t('system_updates.admin.field_hl_title') }} *</span
															>
															<input
																v-model="item['title_' + code]"
																class="su-field__input"
																:class="{
																	'has-error': err('highlights.' + index + '.title_' + code),
																}"
																type="text"
																:maxlength="limits.highlightTitle"
																@input="clearError('highlights.' + index + '.title_' + code)"
															/>
															<span
																v-if="err('highlights.' + index + '.title_' + code)"
																class="su-field__error"
																>{{ err('highlights.' + index + '.title_' + code) }}</span
															>
														</label>
														<label class="su-field">
															<span class="su-field__label">{{
																$t('system_updates.admin.field_hl_subtitle')
															}}</span>
															<input
																v-model="item['subtitle_' + code]"
																class="su-field__input"
																:class="{
																	'has-error': err('highlights.' + index + '.subtitle_' + code),
																}"
																type="text"
																:maxlength="limits.highlightSubtitle"
																@input="clearError('highlights.' + index + '.subtitle_' + code)"
															/>
															<span
																v-if="err('highlights.' + index + '.subtitle_' + code)"
																class="su-field__error"
																>{{ err('highlights.' + index + '.subtitle_' + code) }}</span
															>
														</label>
													</div>
													<div class="su-field">
														<span class="su-field__label">{{
															$t('system_updates.admin.field_hl_description')
														}}</span>
														<div
															class="su-editor su-editor--small"
															:class="{
																'has-error': err('highlights.' + index + '.description_' + code),
															}"
														>
															<vue-editor
																v-model="item['description_' + code]"
																:editor-toolbar="editorToolbar"
																@text-change="
																	clearError('highlights.' + index + '.description_' + code)
																"
															/>
														</div>
														<span
															v-if="err('highlights.' + index + '.description_' + code)"
															class="su-field__error"
															>{{ err('highlights.' + index + '.description_' + code) }}</span
														>
														<span v-else class="su-field__hint"
															>{{ htmlTextLength(item['description_' + code]) }}/{{
																limits.highlightDescription
															}}</span
														>
													</div>
												</div>
											</div>
										</div>

										<button type="button" class="su-add-btn" @click="addHighlight">
											<svg
												width="18"
												height="18"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2.2"
												stroke-linecap="round"
											>
												<path d="M12 5v14M5 12h14" />
											</svg>
											{{ $t('system_updates.admin.add_highlight') }}
										</button>
									</section>

									<!-- Картинки -->
									<section class="su-form__section">
										<h3 class="su-form__section-title">
											{{ $t('system_updates.admin.section_images') }}
										</h3>
										<div class="su-images">
											<div v-for="field in imageFields" :key="field.key" class="su-image">
												<span class="su-field__label">{{ field.label }}</span>
												<div
													class="su-image__frame"
													:class="[
														'su-image__frame--' + field.key,
														{ 'has-error': err(field.key) },
													]"
												>
													<img
														v-if="imageSrc(field.key)"
														:src="imageSrc(field.key)"
														alt=""
														@error="images[field.key].failed = true"
													/>
													<span v-else class="su-image__placeholder">
														<UpdateIcon
															:name="(form.highlights[0] && form.highlights[0].icon) || 'document'"
															:size="36"
														/>
													</span>
													<span v-if="!hasOwnImage(field.key)" class="su-image__default">
														{{ $t('system_updates.admin.image_default') }}
													</span>
												</div>
												<div class="su-image__actions">
													<button
														type="button"
														class="su-admin-btn su-admin-btn--ghost"
														@click="pickImage(field.key)"
													>
														{{
															hasOwnImage(field.key)
																? $t('system_updates.admin.image_replace')
																: $t('system_updates.admin.image_choose')
														}}
													</button>
													<button
														v-if="hasOwnImage(field.key)"
														type="button"
														class="su-admin-link su-admin-link--danger"
														@click="removeImage(field.key)"
													>
														{{ $t('system_updates.admin.image_remove') }}
													</button>
													<input
														:ref="'file_' + field.key"
														type="file"
														accept="image/jpeg,image/png,image/webp"
														class="d-none"
														@change="onImageChange(field.key, $event)"
													/>
												</div>
												<span v-if="err(field.key)" class="su-field__error">{{
													err(field.key)
												}}</span>
												<span v-else class="su-field__hint">{{ field.hint }}</span>
											</div>
										</div>
									</section>

									<div class="su-form__footer">
										<router-link :to="backLink" class="su-admin-btn su-admin-btn--ghost">
											{{ $t('button.cancel') }}
										</router-link>
										<button
											type="submit"
											class="su-admin-btn su-admin-btn--primary"
											:disabled="saving"
										>
											<v-progress-circular
												v-if="saving"
												:width="2"
												:size="18"
												indeterminate
												color="white"
											/>
											<span v-else>{{ $t('button.save') }}</span>
										</button>
									</div>
								</form>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
